/* HOW TO USE

example:
<s-search-staff
  :select.sync="euid"
  :staff.sync="staff"
  label="主管"
/>
*/

<template>
  <!-- don't cache-items, it will cause the emitChange method cannot find the staff object to emit -->
  <v-autocomplete
    ref="autocomplete"
    class="search-staff"
    :value="select"
    :items="items"
    :loading="isLoading"
    :search-input.sync="search"
    hide-no-data
    item-text="description"
    item-value="id"
    :label="label"
    :placeholder="placeholder"
    :outlined="outlined"
    :clearable="clearable"
    :autofocus="autofocus"
    :hint="showGroup ? $t('SearchStaff.OnlyShowStaffWithEuidAndValidGroup') : $t('SearchStaff.OnlyShowStaffWithEuid')"
    :rules="rules"
    :disabled="disabled"
    persistent-hint
    @change="emitChange($event)"
    @input="onInput"
  >
    <template v-slot:selection="{item}">
      <div
        v-if="isShowSelection"
        style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; pointer-events: none; width: calc(99% - 1px);"
      >
        {{ staffToString(item) }}
      </div>
    </template>
  </v-autocomplete>
</template>

<style>
  .v-autocomplete.v-select.v-input--is-focused.search-staff input {
    min-width: 0;
    min-width: initial;
  }
</style>

<script>
// import { mapState } from 'vuex'
import g2eApi from '../api/hr/g2e'
import _ from 'lodash'
import { SEARCH_STAFF_RECORD_TYPE } from '@/constant/constant'
import { nvl } from '@/utils/stringUtil'

export default {
  props: {
    label: {
      type: String,
      default: null
    },
    select: { // selected value, in this component is euid or groupId
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    outlined: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    staff: {
      type: Object,
      default: null
    },
    showSupportStaff: {
      type: Boolean,
      default: false
    },
    showGroup: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: function () {
        return []
      }
    }
  },
  data: function () {
    return {
      staffs: [],
      isLoading: false,
      search: null, // String: searching key word
      isShowSelection: true // String: if it show selection text div. It will be 'false' when tying and will be 'true' when searchnig key word is null
    }
  },

  computed: {
    items () {
      return this.staffs.map(staff => {
        const description = this.staffToString(staff)
        let id = staff.euid
        if (staff.recordType && (staff.recordType === SEARCH_STAFF_RECORD_TYPE.GROUP)) {
          id = staff.definedGroupId + ''
        }
        return Object.assign({}, staff, { description, id })
      })
    }
  },
  watch: {
    search: function (val) {
      // For display
      if (val === null || val === '') {
        this.isShowSelection = true
      } else {
        this.isShowSelection = false
      }

      // Call backend to search
      // if it don't have search keyword, don't search
      if (val === null || val === '') return
      this.isLoading = true
      this.debounceGetStaffs(val)
    },
    select: function (val) {
      this.onSelectChange(val)
    }
  },
  created: function () {
    this.debounceGetStaffs = _.debounce(this.getStaffs, 500)
  },
  mounted: function () {
    // To support default value (euid)
    // prefer a search in order to update the staff list
    if (this.select !== null) {
      this.onSelectChange(this.select)
    }
  },
  methods: {
    onSelectChange: async function (val) {
      // To support update select (euid or groupId) from component outside
      // only if the val not in the result list, perform search
      if (val === null || val === '') return
      // in case group id, change it to string
      val = val + ''
      let isEuidInItems = false
      this.items.forEach(staff => {
        if (staff.id === val) {
          isEuidInItems = true
        }
      })

      // should only happens when the select is set by program outside
      if (!isEuidInItems) {
        // this.search = val
        this.isLoading = true
        let searchByGroupId = !val.startsWith('E')
        await this.getStaffs(val, searchByGroupId)
        this.emitChange(val)
      }
    },
    staffToString: function (staff) {
      if (staff.recordType && (staff.recordType === SEARCH_STAFF_RECORD_TYPE.GROUP)) {
        return `${staff.definedGroupAbbr} - ${staff.definedGroupChinName} ${staff.definedGroupPortName}`
      } else {
        return `${nvl(staff.portName, '')} ${nvl(staff.chinName, '')} ${staff.nickName ? '(' + staff.nickName + ')' : ''} ${staff.euid}`
      }
    },
    getStaffs: function (val, searchByGroupId = false) {
      let cb = response => {
        if (response.length === 0) {
          this.staffs = []
        } else {
          this.staffs = response
        }
      }
      let finalCb = () => {
        this.isLoading = false
      }
      if (this.showSupportStaff && this.showGroup) {
        return g2eApi.searchStaffsWithEntityAdministrativeSupportAndDefinedGroups(cb, finalCb, val, searchByGroupId)
      } else if (this.showSupportStaff) {
        return g2eApi.searchStaffsWithEntityAdministrativeSupport(cb, finalCb, val)
      } else if (this.showGroup) {
        return g2eApi.searchStaffsWithDefinedGroups(cb, finalCb, val, searchByGroupId)
      } else {
        return g2eApi.searchStaff(cb, finalCb, val)
      }
    },
    onInput: function (val) {
      this.isShowSelection = true
    },
    emitChange: function (event) {
      let tStaffArray = this.items.filter(staff => staff.id === event)
      let tStaff = null
      if (tStaffArray.length) {
        tStaff = tStaffArray[0]
      }
      if (tStaff === null) {
        // if the selected euid doesn't exist in the result list, clear the selected value
        event = null
      }
      this.$emit('update:select', event)
      this.$emit('update:staff', tStaff)
    }
  }
}
</script>
